import { ArrowUpRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type {
  KnowledgeGapQuestion,
  KnowledgeGapsResult,
  TopicSignal,
} from "@/types/knowledge/knowledge-brain";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface KnowledgeGapsPanelProps {
  gaps: KnowledgeGapsResult;
  isLoading: boolean;
  hasMore: boolean;
  isScanningMore: boolean;
  onScanMore: () => void;
  /** Opens the upload flow pre-titled with the unanswered question. */
  onAnswerQuestion: (question: KnowledgeGapQuestion) => void;
  /** Opens the guided coverage questions. */
  onOpenPrompts: () => void;
}

const truncate = (text: string, max: number) =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

const TopicRow = ({ topics, tone }: { topics: TopicSignal[]; tone: "confident" | "thin" }) => {
  if (topics.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        {tone === "confident"
          ? "Nothing confirmed confident yet — it fills in as calls are reviewed."
          : "No thin topics spotted in this window."}
      </p>
    );
  }

  const className =
    tone === "confident"
      ? "bg-genie-primary/10 text-genie-primary"
      : "bg-genie-secondary/10 text-genie-secondary";

  return (
    <ul className="flex flex-wrap gap-2">
      {topics.map((topic) => (
        <li key={topic.label}>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
            {topic.label}
            {topic.mentions > 1 && (
              <span className="ml-1 opacity-70 tabular-nums">×{topic.mentions}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
};

const FailedQuestionRow = ({
  question,
  onAnswer,
}: {
  question: KnowledgeGapQuestion;
  onAnswer: () => void;
}) => (
  <li className="rounded-xl border border-gray-200 p-4 transition-colors hover:border-genie-primary/40">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-genie-secondary">
        Caller asked
      </span>
      {question.agentName && (
        <span className="text-[11px] text-gray-500">on {question.agentName}</span>
      )}
      {question.occurrences > 1 && (
        <span className="rounded-full bg-genie-accent/15 px-2 py-0.5 text-[10px] font-bold text-genie-accent-dark">
          asked {question.occurrences} times
        </span>
      )}
    </div>

    <p className="mt-1.5 text-sm font-medium leading-relaxed text-gray-900">
      &ldquo;{truncate(question.question, 180)}&rdquo;
    </p>
    <p className="mt-2 text-xs leading-relaxed text-gray-500">
      <span className="font-semibold text-gray-600">Genie:</span>{" "}
      <span className="italic">&ldquo;{truncate(question.agentReply, 140)}&rdquo;</span>
    </p>

    <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
      <Link
        to={`/genie-hub/conversations/${question.conversationId}`}
        className="text-xs text-gray-500 transition-colors hover:text-genie-primary"
      >
        Open the call
      </Link>
      <Button
        onClick={onAnswer}
        className="h-8 bg-gradient-to-r from-genie-primary to-genie-secondary px-4 text-xs font-semibold text-white shadow-md shadow-genie-primary/20 transition-all hover:from-genie-primary-hover hover:to-genie-secondary hover:shadow-lg"
      >
        Answer this
      </Button>
    </div>
  </li>
);

/**
 * Opens the Knowledge Base directly on what the Genies could not answer.
 * No button, no sheet in the way — the failed questions, the topics that are
 * holding, and the topics that are thin, on the page.
 */
const KnowledgeGapsPanel = ({
  gaps,
  isLoading,
  hasMore,
  isScanningMore,
  onScanMore,
  onAnswerQuestion,
  onOpenPrompts,
}: KnowledgeGapsPanelProps) => (
  <section
    className="rounded-2xl border border-gray-200 bg-white"
    aria-labelledby="knowledge-gaps-heading"
  >
    <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between md:px-6">
      <div className="flex items-start gap-3">
        <GenieIcon
          name="chat-check"
          size={24}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label="Knowledge gaps"
          className="mt-0.5 flex-shrink-0"
        />
        <div>
          <h2 id="knowledge-gaps-heading" className="text-base font-semibold text-gray-900">
            What your Genies could not answer
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {isLoading
              ? "Reading recent calls…"
              : `From ${gaps.conversationsScanned} recent ${
                  gaps.conversationsScanned === 1 ? "call" : "calls"
                } in the last 30 days.`}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpenPrompts}
        className="inline-flex flex-shrink-0 items-center gap-1 self-start text-xs font-semibold text-genie-primary hover:underline sm:self-auto"
      >
        Ask a coverage question
        <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>

    <div className="space-y-5 px-5 py-5 md:px-6">
      <div>
        <h3 className="mb-2.5 text-sm font-semibold text-gray-900">Failed questions</h3>
        {isLoading ? (
          <div className="flex items-center gap-3 py-6 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
            Looking for moments your Genie hesitated…
          </div>
        ) : gaps.failedQuestions.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
            No hesitations found in the calls scanned. Add more knowledge to stay ahead of the
            questions that have not been asked yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {gaps.failedQuestions.map((question) => (
              <FailedQuestionRow
                key={question.id}
                question={question}
                onAnswer={() => onAnswerQuestion(question)}
              />
            ))}
          </ul>
        )}

        {hasMore && !isLoading && (
          <button
            type="button"
            onClick={onScanMore}
            disabled={isScanningMore}
            className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-genie-primary hover:underline disabled:opacity-60"
          >
            {isScanningMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Scan more calls
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Topics it handles well</h3>
          <TopicRow topics={gaps.confidentTopics} tone="confident" />
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Topics running thin</h3>
          <TopicRow topics={gaps.thinTopics} tone="thin" />
        </div>
      </div>
    </div>
  </section>
);

export default KnowledgeGapsPanel;
